import React, { useState } from "react";
import { EditDraftFormLocalizedTitlePart } from "../../draft/DraftFormTitlePart";
import { DraftAlbum, Genre, RegionSettings, TrackReleaseSet } from "../../../../api/useMusicApi";
import { ArtistType, DraftFormArtistPart } from "../../draft/DraftFormArtistPart";
import { Input } from "../../../../components/Input";
import { InputMultiSelect } from "../../../../components/InputMultiSelect";
import { InputCheckbox } from "../../../../components/InputCheckbox";
import { useTranslation } from "../../../../hooks/useTranslation";
import { UpdateFields } from "../../../../hooks/use-advanced-state";
import { PartialNullable } from "../../../../types";
import { ValidationError, ValidationInputWrapper } from "../../../../components/ValidationInputWrapper";
import { CopyFieldValueButton } from "../../../../components/CopyFieldValueButton";
import { TrackFinancialModels } from "./financialModels/TrackFinancialModels";
import { maskitoNumberOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { Button } from "../../../../components/Button";
import { ReactPopover } from "./ReactPopover";
import { SimpleViewModal } from "../../../../components/Modals/SimpleViewModal";
import { useAuth } from "../../../../contexts/AuthContext";
import { Link } from "../../../../components/Link";
import { InputLabel } from "../../../../components/InputLabel";
import { faExternalLink } from "@fortawesome/free-solid-svg-icons";

type Props = {
  isDraft: boolean;
  index: number;
  album: PartialNullable<DraftAlbum>;
  item: PartialNullable<TrackReleaseSet>;
  genres: Genre[];
  validationErrors: ValidationError[];
  onChange: UpdateFields<TrackReleaseSet>;
  onChangeForAll: (field: keyof TrackReleaseSet | string, value?: any) => void;
};

export const TrackItemForm: React.FC<Props> = ({
                                                 isDraft,
                                                 item,
                                                 album,
                                                 index,
                                                 validationErrors,
                                                 genres,
                                                 onChange,
                                                 onChangeForAll
                                               }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [showTextModal, setShowTextModal] = useState<boolean>(false);
  const rightPercentInputRef = useMaskito({
    options: maskitoNumberOptionsGenerator({
      max: 100,
      min: 0,
      precision: 2,
      decimalSeparator: "."
    })
  });

  const startPointInputRef = useMaskito({
    options: maskitoNumberOptionsGenerator({
      min: 0,
      precision: 0,
      thousandSeparator: ""
    })
  });

  return (
    <div className={"mt-4 mx-3 text-sm flex flex-col 2xl:flex-row gap-5"}>
      <div className={"w-full xl:w-full 2xl:w-8/12"}>
        <EditDraftFormLocalizedTitlePart
          disabled={!isDraft}
          entity={item}
          validationErrorPrefix={`${index}`}
          validationErrors={validationErrors}
          regionSettings={RegionSettings.ALL}
          onChange={onChange as any}
          onChangeForAll={onChangeForAll}
        />

        <div className={"mt-2 flex w-full gap-3"}>
          <div className={"flex-1"}>
            <ValidationInputWrapper
              isWrap
              wrapErrorText={t("has_error").toString()}
              attribute={`${index}.country_meta.artists.main`}
              validationErrors={validationErrors}
            >
              <DraftFormArtistPart
                disabled={!isDraft}
                type={ArtistType.MAIN}
                draft={item}
                validationErrorPrefix={`${index}`}
                validationErrors={validationErrors}
                onChange={onChange}
                onChangeForAll={onChangeForAll}
              />
            </ValidationInputWrapper>
          </div>
          <div className={"flex-1"}>
            <ValidationInputWrapper
              isWrap
              wrapErrorText={t("has_error").toString()}
              attribute={`${index}.country_meta.artists.featured`}
              validationErrors={validationErrors}
            >
              <DraftFormArtistPart
                disabled={!isDraft}
                type={ArtistType.FEATURED}
                validationErrorPrefix={`${index}`}
                validationErrors={validationErrors}
                draft={item}
                onChange={onChange}
                onChangeForAll={onChangeForAll}
              />
            </ValidationInputWrapper>
          </div>
        </div>

        <hr className={"my-4"} />

        <div className={"mt-4"}>
          <div className={"mt-3 flex gap-3"}>
            <div className={"flex-1"}>
              <ValidationInputWrapper
                attribute={`${index}.code_isrc`}
                validationErrors={validationErrors}
              >
                <Input
                  disabled={!isDraft}
                  className={"my-2"}
                  label={`${t("isrc_available")}`}
                  labelHint={t("isrc_hint").toString()}
                  validationError={
                    validationErrors.find(
                      (v) => v.attr === `${index}.code_isrc`
                    )?.detail ?? null
                  }
                  value={item?.code_isrc ?? ""}
                  onChange={(value) => onChange({ code_isrc: value })}
                />
              </ValidationInputWrapper>
              <ValidationInputWrapper
                attribute={`${index}.external_id`}
                validationErrors={validationErrors}
              >
                <Input
                  disabled={!isDraft}
                  className={"mt-2"}
                  validationError={
                    validationErrors.find(
                      (v) => v.attr === `${index}.external_id`
                    )?.detail ?? null
                  }
                  label={`${t("external_id")}`}
                  labelHint={t("external_id_track_hint").toString()}
                  value={item?.external_id ?? ""}
                  onChange={(value) => onChange({ external_id: value })}
                />
              </ValidationInputWrapper>
            </div>

            <div className={"flex-1"}>
              <ValidationInputWrapper
                attribute={`${index}.composer`}
                validationErrors={validationErrors}
              >
                <Input
                  disabled={!isDraft}
                  className={"my-2"}
                  label={`${t("music_owner")}`}
                  labelHint={t("composer_hint").toString()}
                  validationError={
                    validationErrors.find((v) => v.attr === `${index}.composer`)
                      ?.detail ?? null
                  }
                  value={item?.composer ?? ""}
                  labelAdditionalContent={
                    <CopyFieldValueButton
                      visible={isDraft}
                      fieldPath={"composer"}
                      onChangeForAll={onChangeForAll ?? null}
                    />
                  }
                  onChange={(value) =>
                    onChange(
                      (prevState) =>
                        ({
                          ...prevState,
                          composer: value
                        }) as any
                    )
                  }
                />
              </ValidationInputWrapper>
              <ValidationInputWrapper
                attribute={`${index}.lyric`}
                validationErrors={validationErrors}
              >
                <Input
                  disabled={!isDraft}
                  className={"my-2"}
                  label={`${t("lyrics_owner")}`}
                  labelHint={t("lyricist_hint").toString()}
                  labelAdditionalContent={
                    <CopyFieldValueButton
                      visible={isDraft}
                      fieldPath={"lyric"}
                      onChangeForAll={onChangeForAll ?? null}
                    />
                  }
                  value={item?.lyric ?? ""}
                  validationError={
                    validationErrors.find((v) => v.attr === `${index}.lyric`)
                      ?.detail ?? null
                  }
                  onChange={(value) =>
                    onChange(
                      (prevState) =>
                        ({
                          ...prevState,
                          lyric: value
                        }) as any
                    )
                  }
                />
              </ValidationInputWrapper>
            </div>
          </div>
          <hr className={"my-4"} />
          <div className={"mt-4 w-full flex align-items-center gap-3"}>
            <div className={"flex-1"}>
              <ValidationInputWrapper
                attribute={`${index}.genres`}
                validationErrors={validationErrors}
              >
                <InputMultiSelect
                  required
                  disabled={!isDraft}
                  placeholder={""}
                  max={3}
                  hasError={
                    validationErrors.find((v) => v.attr === `${index}.genres`)
                      ?.detail != null
                  }
                  labelHint={`${t("genres_and_subgenres_placeholder")}`}
                  label={`${t("genres_and_subgenres_title")}`}
                  labelAdditionalContent={
                    <CopyFieldValueButton
                      visible={isDraft}
                      fieldPath={"genres"}
                      className={"ml-2"}
                      onChangeForAll={onChangeForAll ?? null}
                    />
                  }
                  defaultValues={item?.genres ?? []}
                  options={genres.map((g) => ({ label: g?.title, value: g?.id }))}
                  onChange={(items) =>
                    onChange({
                      genres: items.map((i) => i.value)
                    })
                  }
                />
              </ValidationInputWrapper>
            </div>
            <div style={{ marginTop: 25 }} className={"flex-1"}>
              <div>
                <div>
                  <ValidationInputWrapper
                    attribute={`${index}.is_explicit`}
                    validationErrors={validationErrors}
                  >
                    <InputCheckbox
                      disabled={!isDraft}
                      label={`${t("explicit_is")}`}
                      checked={item?.is_explicit ?? false}
                      labelHint={t("is_explicit_hint").toString()}
                      onChange={(value) => onChange({ is_explicit: value })}
                    />
                  </ValidationInputWrapper>
                </div>
                <div>
                  <ValidationInputWrapper
                    attribute={`${index}.focus`}
                    validationErrors={validationErrors}
                  >
                    <InputCheckbox
                      disabled={!isDraft}
                      label={`${t("focus")}`}
                      labelHint={t("focus_track_hint").toString()}
                      checked={item?.focus ?? false}
                      onChange={(value) => onChange({ focus: value })}
                    />
                  </ValidationInputWrapper>
                </div>
              </div>
            </div>

          </div>
          <div className={"mt-3 flex gap-3"}>
            <div className={"flex-1"}>
              <ValidationInputWrapper
                attribute={`${index}.right_author_percentage`}
                validationErrors={validationErrors}
              >
                {
                  isDraft && <Input
                    ref={rightPercentInputRef}
                    disabled={!isDraft}
                    onWheel={(e) => (e.target as any).blur()}
                    className={"my-2"}
                    label={`${t("copyright_share")}`}
                    labelHint={t("right_author_percentage_hint").toString()}
                    labelAdditionalContent={
                      <CopyFieldValueButton
                        visible={isDraft}
                        fieldPath={"right_author_percentage"}
                        onChangeForAll={onChangeForAll ?? null}
                      />
                    }
                    validationError={
                      validationErrors.find(
                        (v) => v.attr === `${index}.right_author_percentage`
                      )?.detail ?? null
                    }
                    value={item?.right_author_percentage ?? undefined}
                    onInput={(e) => {
                      const value = e.currentTarget.value;
                      if (value === "") {
                        onChange({ right_author_percentage: null });
                        return;
                      }

                      onChange({ right_author_percentage: value });
                    }}
                    onChange={(value) => undefined}
                  />
                }
                {
                  !isDraft && <div className={"flex flex-col"}>
                    <InputLabel
                      className={{ label: "mb-2" }}
                      label={`${t("copyright_share")}`}
                      labelHint={t("right_author_percentage_hint").toString()}
                    />
                    <Link to={`/rights?release_id_list=${item.id}`} target={"_blank"}>
                      <Button
                        className={"w-full text-[12px]"}
                        label={t("go_to_rights_page_for_view_percentage").toString()}
                        icon={faExternalLink}
                        iconDirection={"right"}
                      />
                    </Link>
                  </div>
                }
              </ValidationInputWrapper>
            </div>
            <div className={"flex items-end flex-1"}>
              {album.content_type === "audio" && (
                <div className={"w-full"}>
                  <ValidationInputWrapper
                    attribute={`${index}.country_meta.start_point`}
                    validationErrors={validationErrors}
                  >
                    <Input
                      ref={startPointInputRef}
                      disabled={!isDraft}
                      labelHint={t("start_point_description").toString()}
                      validationError={
                        validationErrors.find(
                          (v) => v.attr === `${index}.country_meta.start_point`
                        )?.detail ?? null
                      }
                      label={`${t("start_point")}`}
                      className={"my-2"}
                      defaultValue={item?.country_meta?.start_point ?? ""}
                      onChange={() => null}
                      onInput={(e) => {
                        const value = e.currentTarget.value;
                        onChange((prevState) => ({
                          ...prevState,
                          country_meta: {
                            ...(prevState?.country_meta ?? {}),
                            start_point: value
                          } as any
                        }));
                      }}
                    />
                  </ValidationInputWrapper>
                </div>
              )}
              {
                user?.is_uma_employee && item.text != null &&
                <div className={"py-[8px] ml-2"}>
                  <ReactPopover
                    trigger={"hover"}
                    content={(
                      <div className={"text-muted flex justify-center"}>
                        {t("lyrics")}
                      </div>
                    )}
                  >
                    <Button onClick={() => setShowTextModal(true)}>
                      {t("text")}
                    </Button>
                  </ReactPopover>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
      <TrackFinancialModels
        isDraft={isDraft}
        contentType={album.content_type as any ?? "audio"}
        defaultDate={album?.release_date ?? null}
        services={item?.services ?? {}}
        onChange={onChange}
        onChangeForAll={onChangeForAll}
      />
      {
        showTextModal &&
        <SimpleViewModal
          showButtons={false}
          title={t("lyrics")}
          className={"w-9/12 lg:w-7/12 xl:w-4/12"}
          onCancel={() => setShowTextModal(false)}>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {item?.text}
          </div>
        </SimpleViewModal>
      }
    </div>
  );
};
